import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type MapView from '@arcgis/core/views/MapView';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { useNearestByGeometry } from './useNearestByGeometry';
import { useNearestPropertyHoverLine } from './useNearestPropertyHoverLine';
import { NearestPropertiesTable } from './NearestPropertiesTable';
import type { NearestPropertyRow } from './types';

// ─── Props ───────────────────────────────────────────────────────────────────

/**
 * Already documented with JSDoc above the interface declaration.
 */
export interface NearestPropertiesPanelProps {
  /** JSON-serialized source geometry (Polygon / Extent) to measure distances from. */
  sourceGeometry: Record<string, unknown> | null | undefined;
  /** Target FeatureLayer whose features are the distance candidates. */
  targetLayer: FeatureLayer | undefined;
  /** MapView — needed for line drawing and zoom-to on click. */
  view: MapView | undefined;
  /** Optional WHERE clause to filter the target layer before computing distances. */
  where?: string;
  /** Maximum number of results. Default: 5. */
  limit?: number;
  /** Section title. Default: 'Nearest Properties'. */
  title?: string;
  /** Max rows visible in the scrollable table. Default: 10. */
  maxRows?: number;
  /**
   * Determines how row clicks navigate:
   * - `'property'` → `/property/{propId}` (owned property)
   * - `'investment'` → `/invest/transaction/{transactionId}` (acquisition)
   *
   * Default: `'property'`
   */
  navigationType?: 'property' | 'investment';
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Self-contained "smart" panel that computes nearest features from a source
 * geometry against a target FeatureLayer, draws hover/persistent lines on
 * the map, and renders a filterable results table.
 *
 * Usage:
 * ```tsx
 * <NearestPropertiesPanel
 *   sourceGeometry={propertyGeomJSON}
 *   targetLayer={boundariesLayer}
 *   view={view}
 *   title="Nearest Managed Properties"
 *   limit={5}
 * />
 *
 * <NearestPropertiesPanel
 *   sourceGeometry={propertyGeomJSON}
 *   targetLayer={acquisitionLayer}
 *   view={view}
 *   where="Status='Active'"
 *   title="Nearest Active Acquisitions"
 *   navigationType="investment"
 *   limit={5}
 * />
 * ```
 */
export function NearestPropertiesPanel({
  sourceGeometry,
  targetLayer,
  view,
  where,
  limit = 5,
  title = 'Nearest Properties',
  maxRows = 10,
  navigationType = 'property',
}: NearestPropertiesPanelProps) {
  const navigate = useNavigate();

  // Derive skip internally — no data if source geometry or target layer missing
  const skip = !sourceGeometry || !targetLayer;

  // ── Data fetching + distance computation ──────────────────────────────────
  const { rows, isLoading, error } = useNearestByGeometry({
    sourceGeometry,
    targetLayer,
    where,
    limit,
    skip,
  });

  // ── Map line drawing (internalized) ───────────────────────────────────────
  const {
    handleHoverRow,
    showAllLines,
    toggleShowAllLines,
    setAllLineRows,
  } = useNearestPropertyHoverLine({
    view,
    boundariesLayer: targetLayer,
  });

  // Feed rows into the hover-line hook for color assignment + "show all" rendering
  const coloredRows = useMemo(
    () => setAllLineRows(rows),
    [rows, setAllLineRows],
  );

  // ── Zoom to a row's feature on the map ────────────────────────────────────
  const handleZoomToRow = useCallback(
    (row: NearestPropertyRow) => {
      if (!view || !targetLayer) return;
      targetLayer
        .queryFeatures({
          outSpatialReference:view.spatialReference,
          where: `OBJECTID=${row.objectId}`,
          returnGeometry: true,
          outFields: [],
        })
        .then((result) => {
          const extent = result.features[0]?.geometry?.extent;
          if (extent) view.goTo(extent.expand(1.5), { duration: 400 });
        });
    },
    [view, targetLayer],
  );

  // ── Navigate to a row's detail page ───────────────────────────────────────
  const handleNavigateRow = useCallback(
    (row: NearestPropertyRow) => {
      if (navigationType === 'investment' && row.transactionId) {
        navigate(`/invest/transaction/${row.transactionId}`);
      } else if (row.propId) {
        navigate(`/property/${row.propId}`);
      }
    },
    [navigate, navigationType],
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <NearestPropertiesTable
      rows={coloredRows}
      isLoading={isLoading}
      error={error}
      maxRows={maxRows}
      title={title}
      onHoverRow={handleHoverRow}
      onZoomToRow={handleZoomToRow}
      onNavigateRow={handleNavigateRow}
      showAllLines={showAllLines}
      onToggleShowAllLines={toggleShowAllLines}
    />
  );
}
